import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Settings, Search, Sparkles } from "lucide-react";

import {
  fetchAutoSuggestPairs,
  fetchEventRecommendations,
  fetchRecommendCandidates,
  fireOrganizerRecPush,
  recommendPairBidirectional,
  sendCompleteProfileInvite,
  unrecommendPairBidirectional,
  type AutoSuggestPairRow,
  type RecommendCandidateRow,
} from "@/lib/api/recommendations";
import {
  fetchEventMatchesForOrganizer,
  revealMatchManually,
  type OrganizerMatchRow,
} from "@/lib/api/organizer-matches";
import { isRevealed as isEventRevealed } from "@/lib/reveal";
import { env } from "@/lib/env";

/**
 * Fire-and-forget D1/D2 Telegram push to the participant on
 * moderation decisions. Swallows errors — the email channel is the
 * authoritative one for rejection; the TG push is an extra nudge.
 */
function firePitchModerationPush(
  participantId: string,
  decision: "approved" | "needs_changes",
  reason?: string,
): void {
  const endpoint = `${env.VITE_SUPABASE_URL}/functions/v1/send-meetup-pitch-moderation-push`;
  fetch(endpoint, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      apikey: env.VITE_SUPABASE_ANON_KEY,
      Authorization: `Bearer ${env.VITE_SUPABASE_ANON_KEY}`,
    },
    body: JSON.stringify({
      participant_id: participantId,
      decision,
      reason,
    }),
  }).catch(() => {
    /* swallow */
  });
}

import { fetchEventById, type EventRow } from "@/lib/api/events";
import {
  addParticipantManually,
  fetchParticipants,
  updatePitchStatus,
  type ParticipantRow,
} from "@/lib/api/participants";
import {
  getOrganizerVideoUrl,
  triggerMatchScoring,
} from "@/lib/api/organizer-actions";
import { EventQR } from "@/features/event-qr/EventQR";
import { TelegramBroadcastButton } from "@/components/organizer/TelegramBroadcastButton";
import {
  enqueueMeetupEmail,
  formatMeetupDate,
  meetupEventUrl,
} from "@/lib/api/meetup-emails";

import { Button, LinkButton } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { Modal } from "@/components/ui/Modal";
import { Field } from "@/components/ui/Field";
import { Input, Textarea } from "@/components/ui/Input";
import { PageHeadline } from "@/components/ui/Headline";
import { Loader, EmptyState, InlineAlert } from "@/components/ui/States";
import { Avatar } from "@/components/ui/Avatar";
import { VideoAvatar } from "@/components/ui/VideoAvatar";
import { OrganizerShell } from "@/components/layout/OrganizerShell";
import {
  PreRevealState,
  LiveState,
  WrappingUpState,
} from "@/features/event-states/EventStates";
import { computeEventTiming } from "@/lib/event-state";

type Tab = "overview" | "pitches" | "analytics" | "participants" | "matches";

function formatEventDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  } catch {
    return iso;
  }
}

function formatCountdown(event: EventRow | null | undefined): { label: string; value: string } {
  if (!event) return { label: "until matches are revealed", value: "—" };
  const revealMs = new Date(event.event_date).getTime() - event.match_reveal_hours * 3600 * 1000;
  const delta = revealMs - Date.now();
  if (delta <= 0) {
    const eventDelta = new Date(event.event_date).getTime() - Date.now();
    if (eventDelta <= 0) return { label: "event status", value: "Finished" };
    return { label: "matches revealed", value: "Live" };
  }
  const hours = Math.floor(delta / (3600 * 1000));
  const minutes = Math.floor((delta % (3600 * 1000)) / (60 * 1000));
  if (hours >= 24) {
    const days = Math.floor(hours / 24);
    return { label: "until matches are revealed", value: `${days}d ${hours % 24}h` };
  }
  return { label: "until matches are revealed", value: `${hours}h ${minutes}m` };
}

export function EventDashboardPage() {
  const { eventId } = useParams<{ eventId: string }>();
  const [tab, setTab] = useState<Tab>("overview");

  const { data: event, isLoading: eventLoading } = useQuery({
    queryKey: ["event", eventId],
    queryFn: () => fetchEventById(eventId ?? ""),
    enabled: Boolean(eventId),
  });

  const { data: participants = [], isLoading: participantsLoading } = useQuery({
    queryKey: ["participants", eventId],
    queryFn: () => fetchParticipants(eventId ?? ""),
    enabled: Boolean(eventId),
  });

  if (eventLoading) {
    return (
      <OrganizerShell>
        <Loader label="Loading event..." />
      </OrganizerShell>
    );
  }

  if (!event) {
    return (
      <OrganizerShell>
        <Card padding="lg" className="mt-8 text-center">
          <EmptyState
            icon="🔎"
            title="Event not found"
            description="The link may be outdated or the event was deleted."
            action={
              <LinkButton to="/events" variant="secondary">
                ← Back to My Events
              </LinkButton>
            }
          />
        </Card>
      </OrganizerShell>
    );
  }

  return (
    <OrganizerShell>
      <div className="animate-fade-in">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            <PageHeadline title={event.title} subtitle={formatEventDate(event.event_date)} />
            {event.location ? (
              <p className="mt-1 break-words text-[13px] text-[#94a3b8]">{event.location}</p>
            ) : null}
          </div>
          <LinkButton
            to={`/events/${event.id}/settings`}
            variant="secondary"
            size="md"
            leadingIcon={<Settings className="h-4 w-4" />}
          >
            Settings
          </LinkButton>
        </div>

        <div className="-mx-4 mt-6 flex gap-1 overflow-x-auto border-b border-[#e2e8f0] px-4 sm:mx-0 sm:px-0">
          {(["overview", "pitches", "matches", "analytics", "participants"] as Tab[]).map((t) => (
            <TabButton key={t} active={tab === t} onClick={() => setTab(t)}>
              {t}
            </TabButton>
          ))}
        </div>

        <div className="mt-6">
          {tab === "overview" && (
            <OverviewTab
              event={event}
              participants={participants}
              loading={participantsLoading}
              onReviewClick={() => setTab("pitches")}
            />
          )}
          {tab === "pitches" && <PitchesTab participants={participants} event={event} />}
          {tab === "matches" && <MatchesTab event={event} />}
          {tab === "analytics" && <AnalyticsTab participants={participants} event={event} />}
          {tab === "participants" && (
            <ParticipantsTab participants={participants} event={event} />
          )}
        </div>
      </div>
    </OrganizerShell>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        "relative shrink-0 whitespace-nowrap px-4 py-3 text-[14px] font-semibold capitalize transition-colors " +
        (active ? "text-[#2563eb]" : "text-[#6c757d] hover:text-[#1a1a1a]")
      }
    >
      {children}
      {active ? (
        <span className="absolute -bottom-px left-0 right-0 h-0.5 rounded-t bg-[#2563eb]" />
      ) : null}
    </button>
  );
}

function OverviewTab({
  event,
  participants,
  loading,
  onReviewClick,
}: {
  event: EventRow;
  participants: ParticipantRow[];
  loading: boolean;
  onReviewClick: () => void;
}) {
  const timing = useMemo(() => computeEventTiming(event), [event]);
  const eventUrlForShare = event.slug ? `https://meetup.finally.plus/e/${event.slug}` : "";

  if (timing.state === "pre_reveal") {
    return (
      <div className="flex flex-col gap-5">
        {eventUrlForShare ? (
          <EventQR url={eventUrlForShare} title={event.title} size={220} />
        ) : null}
        <PreRevealState event={event} participants={participants} />
      </div>
    );
  }
  if (timing.state === "live") {
    const matchesRevealed = participants.filter(
      (p) => p.pitch_status === "approved",
    ).length;
    return (
      <div className="flex flex-col gap-5">
        {eventUrlForShare ? (
          <EventQR url={eventUrlForShare} title={event.title} size={220} />
        ) : null}
        <LiveState
          event={event}
          participants={participants}
          matchesRevealed={matchesRevealed}
          matchesOpened={0}
          markedAsMet={0}
          recentActivity={[]}
          onReportIssue={() =>
            window.open("mailto:support@finally.plus?subject=Event%20issue", "_blank")
          }
        />
      </div>
    );
  }
  if (timing.state === "wrapping_up") {
    return (
      <WrappingUpState
        event={event}
        surveyCounts={{ responded: 0, total: participants.length }}
        preliminaryMetrics={{
          totalMatches: 0,
          markedAsMet: 0,
          nps: null,
          fiveStarPct: null,
        }}
        reminderSentAt={null}
        onSendReminder={() => undefined}
        onViewEarlyReport={() => undefined}
      />
    );
  }

  const total = participants.length;
  const submitted = participants.filter((p) => p.pitch_status !== "pending").length;
  const flagged = participants.filter((p) => p.pitch_status === "flagged").length;
  const needsReview = participants.filter(
    (p) => p.pitch_status === "flagged" || p.pitch_status === "submitted",
  ).length;
  const submittedPct = total > 0 ? Math.round((submitted / total) * 100) : 0;
  const countdown = formatCountdown(event);
  const lowConversion = total > 0 && submittedPct < 50;

  const eventUrl = event.slug ? `https://meetup.finally.plus/e/${event.slug}` : "";

  const copyLink = async () => {
    if (!eventUrl) return;
    try {
      await navigator.clipboard.writeText(eventUrl);
    } catch {
      /* ignore */
    }
  };

  return (
    <div className="flex flex-col gap-5">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <MetricCard value={String(total)} label="participants registered" />
        <MetricCard
          value={`${submitted} (${submittedPct}%)`}
          label="pitches submitted"
          progress={submittedPct}
        />
        <MetricCard
          value={String(flagged)}
          label="pitches flagged by AI"
          tone={flagged > 0 ? "warning" : "neutral"}
        />
        <MetricCard value={countdown.value} label={countdown.label} tone="info" />
      </div>

      {loading ? <Loader label="Loading participants..." /> : null}

      {needsReview > 0 ? (
        <Card tone="flagged" padding="md">
          <div className="flex flex-wrap items-center gap-4">
            <div className="grid h-10 w-10 place-items-center rounded-full bg-white text-[#92400e]">
              !
            </div>
            <div className="flex-1">
              <p className="text-[14px] font-bold text-[#92400e]">
                {needsReview} pitch{needsReview === 1 ? "" : "es"} awaiting your review
              </p>
              <p className="text-[13px] text-[#78350f]">
                {flagged > 0
                  ? `${flagged} flagged by AI — the rest auto-approved, you can still spot-check.`
                  : "Auto-moderation OK — approve or spot-check at your pace."}
              </p>
            </div>
            <Button onClick={onReviewClick} size="md">
              Review Now
            </Button>
          </div>
        </Card>
      ) : total > 0 ? (
        <Card tone="approved" padding="md">
          <div className="flex items-center gap-3">
            <span className="text-2xl">✅</span>
            <div>
              <p className="text-[14px] font-bold text-[#065f46]">Everything looks great</p>
              <p className="text-[13px] text-[#047857]">
                All pitches processed — no action needed.
              </p>
            </div>
          </div>
        </Card>
      ) : null}

      {lowConversion ? (
        <Card tone="flagged" padding="md">
          <div className="flex flex-wrap items-center gap-4">
            <div className="grid h-10 w-10 place-items-center rounded-full bg-white text-[#92400e]">
              ⚠
            </div>
            <div className="flex-1">
              <p className="text-[14px] font-bold text-[#92400e]">
                Only {submittedPct}% have recorded a pitch
              </p>
              <p className="text-[13px] text-[#78350f]">
                A nudge usually doubles the rate. Send a reminder to participants without a pitch.
              </p>
            </div>
            <SendRemindersButton event={event} participants={participants} primary />
          </div>
        </Card>
      ) : null}

      <div className="flex flex-wrap gap-3">
        <Button variant="secondary" onClick={copyLink} disabled={!eventUrl}>
          Copy Event Link
        </Button>
        <SendRemindersButton event={event} participants={participants} />
      </div>

      {/* Notify-via-Telegram lives in its own card so it doesn't get
          buried at the end of a wrapping row on phones (the action bar
          above can hide the third button below the fold on narrow
          screens). Explicit heading + body makes the affordance obvious. */}
      <Card padding="md">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-[11px] font-bold uppercase tracking-wide text-[#94a3b8]">
              Telegram broadcast
            </p>
            <p className="mt-1 text-[14px] font-semibold text-[#1a1a1a]">
              Notify participants via Telegram
            </p>
            <p className="mt-0.5 text-[13px] text-[#6c757d]">
              Pick recipients, write a short note, and we'll DM it through{" "}
              <strong>@finally_meetup_bot</strong>.
            </p>
          </div>
          <TelegramBroadcastButton eventId={event.id} />
        </div>
      </Card>

      {eventUrl ? <EventQR url={eventUrl} title={event.title} size={220} /> : null}
    </div>
  );
}

function SendRemindersButton({
  event,
  participants,
  primary,
}: {
  event: EventRow;
  participants: ParticipantRow[];
  primary?: boolean;
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button variant={primary ? "primary" : "secondary"} onClick={() => setOpen(true)} size="md">
        Send Reminders
      </Button>
      <SendRemindersModal
        open={open}
        event={event}
        participants={participants}
        onClose={() => setOpen(false)}
      />
    </>
  );
}

type Segment = "no_pitch" | "recent" | "all";

function SendRemindersModal({
  open,
  event,
  participants,
  onClose,
}: {
  open: boolean;
  event: EventRow;
  participants: ParticipantRow[];
  onClose: () => void;
}) {
  const [segment, setSegment] = useState<Segment>("no_pitch");
  const [days, setDays] = useState(3);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState<number | null>(null);

  const recipients = useMemo(() => {
    if (segment === "no_pitch") {
      return participants.filter((p) => p.pitch_status !== "approved");
    }
    if (segment === "recent") {
      const cutoff = Date.now() - days * 86_400_000;
      return participants.filter((p) => new Date(p.created_at).getTime() >= cutoff);
    }
    return participants;
  }, [segment, days, participants]);

  async function runSend() {
    if (!event.slug || recipients.length === 0) return;
    setSending(true);
    const eventUrl = meetupEventUrl(event.slug);
    let ok = 0;
    for (const p of recipients) {
      try {
        await enqueueMeetupEmail({
          eventId: event.id,
          toEmail: p.email,
          toName: p.name,
          emailType: "meetup_invitation",
          participantId: p.id,
          payload: {
            name: p.name,
            eventTitle: event.title,
            eventDate: formatMeetupDate(event.event_date),
            eventUrl,
            venue: event.location ?? undefined,
          },
        });
        ok += 1;
      } catch {
        /* per-row failure */
      }
    }
    setSending(false);
    setSent(ok);
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Send reminders"
      description="Pick a segment, preview the recipient count, then queue."
      maxWidth={520}
    >
      <div className="flex flex-col gap-3">
        <SegmentOption
          selected={segment === "no_pitch"}
          onSelect={() => setSegment("no_pitch")}
          title="Participants without an approved pitch"
          count={participants.filter((p) => p.pitch_status !== "approved").length}
        />
        <SegmentOption
          selected={segment === "recent"}
          onSelect={() => setSegment("recent")}
          title={`Registered in the last ${days} day${days === 1 ? "" : "s"}`}
          count={
            participants.filter(
              (p) =>
                new Date(p.created_at).getTime() >= Date.now() - days * 86_400_000,
            ).length
          }
        >
          {segment === "recent" ? (
            <div className="mt-2 flex items-center gap-2">
              <Input
                type="number"
                min={1}
                max={30}
                value={days}
                onChange={(e) => setDays(Math.max(1, Math.min(30, Number(e.target.value) || 1)))}
                className="w-20"
              />
              <span className="text-[13px] text-[#6c757d]">days window</span>
            </div>
          ) : null}
        </SegmentOption>
        <SegmentOption
          selected={segment === "all"}
          onSelect={() => setSegment("all")}
          title="All participants"
          count={participants.length}
        />
      </div>

      {sent !== null ? (
        <InlineAlert tone="success" className="mt-4">
          Queued {sent} reminder{sent === 1 ? "" : "s"} — they'll send within a minute.
        </InlineAlert>
      ) : null}

      <div className="mt-5 flex gap-2">
        <Button variant="secondary" onClick={onClose} size="md">
          Close
        </Button>
        <Button
          onClick={runSend}
          loading={sending}
          disabled={recipients.length === 0}
          fullWidth
          size="md"
        >
          {sending
            ? "Queuing..."
            : `Send to ${recipients.length} participant${recipients.length === 1 ? "" : "s"}`}
        </Button>
      </div>
    </Modal>
  );
}

function SegmentOption({
  selected,
  onSelect,
  title,
  count,
  children,
}: {
  selected: boolean;
  onSelect: () => void;
  title: string;
  count: number;
  children?: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={
        "flex w-full flex-col items-start gap-1 rounded-[12px] border px-4 py-3 text-left transition-colors " +
        (selected
          ? "border-[#2563eb] bg-[#e6f1fb]"
          : "border-[#e2e8f0] bg-white hover:border-[#cbd5e1]")
      }
    >
      <div className="flex w-full items-center justify-between">
        <span className="text-[14px] font-semibold text-[#1a1a1a]">{title}</span>
        <Badge tone={selected ? "primary" : "neutral"}>{count}</Badge>
      </div>
      {children}
    </button>
  );
}

type PitchFilter = "all" | "needs_review" | "approved" | "rejected";

function PitchesTab({
  participants,
  event,
}: {
  participants: ParticipantRow[];
  event: EventRow;
}) {
  const eventId = event.id;
  const [filter, setFilter] = useState<PitchFilter>("needs_review");
  const [watching, setWatching] = useState<ParticipantRow | null>(null);
  const [rejectComment, setRejectComment] = useState<string>("");
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (vars: {
      id: string;
      status: ParticipantRow["pitch_status"];
      participant: ParticipantRow;
      event?: EventRow;
      comment?: string;
    }) => {
      await updatePitchStatus(vars.id, vars.status);
      if (vars.status === "approved") {
        void triggerMatchScoring(vars.id);
        // D1 push — fires alongside the existing match-scoring trigger.
        void firePitchModerationPush(vars.id, "approved");
      }
      if (vars.status === "rejected") {
        void firePitchModerationPush(vars.id, "needs_changes", vars.comment);
      }
      if (vars.status === "rejected" && vars.event?.slug) {
        void enqueueMeetupEmail({
          eventId: vars.event.id,
          toEmail: vars.participant.email,
          toName: vars.participant.name,
          emailType: "meetup_pitch_declined",
          participantId: vars.participant.id,
          payload: {
            name: vars.participant.name,
            eventTitle: vars.event.title,
            eventUrl: meetupEventUrl(vars.event.slug),
            organizerComment: vars.comment ?? undefined,
          },
        });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["participants", eventId] });
      setWatching(null);
      setRejectComment("");
    },
  });

  const withVideo = participants.filter(
    (p) => p.video_storage_path || p.pitch_status !== "pending",
  );

  const counts = {
    all: withVideo.length,
    needs_review: withVideo.filter(
      (p) => p.pitch_status === "flagged" || p.pitch_status === "submitted",
    ).length,
    approved: withVideo.filter((p) => p.pitch_status === "approved").length,
    rejected: withVideo.filter((p) => p.pitch_status === "rejected").length,
  };

  const filtered = withVideo.filter((p) => {
    if (filter === "all") return true;
    if (filter === "needs_review") {
      return p.pitch_status === "flagged" || p.pitch_status === "submitted";
    }
    if (filter === "approved") return p.pitch_status === "approved";
    return p.pitch_status === "rejected";
  });

  const filterChips: { key: PitchFilter; label: string }[] = [
    { key: "all", label: `All (${counts.all})` },
    { key: "needs_review", label: `Needs Review (${counts.needs_review})` },
    { key: "approved", label: `Approved (${counts.approved})` },
    { key: "rejected", label: `Rejected (${counts.rejected})` },
  ];

  return (
    <>
      <div className="mb-4 flex flex-wrap gap-2">
        {filterChips.map((chip) => (
          <button
            key={chip.key}
            onClick={() => setFilter(chip.key)}
            className={
              "rounded-full px-3 py-1.5 text-[13px] font-semibold transition-colors " +
              (filter === chip.key
                ? "bg-[#2563eb] text-white"
                : "border border-[#e2e8f0] bg-white text-[#475569] hover:border-[#cbd5e1]")
            }
          >
            {chip.label}
          </button>
        ))}
      </div>
      <p className="mb-4 text-[13px] text-[#6c757d]">
        Pitches are auto-published if AI finds no issues. Flagged or submitted ones live here until you decide.
      </p>

      {filtered.length === 0 ? (
        <Card padding="lg">
          <EmptyState icon="🎬" title="No pitches in this category yet" />
        </Card>
      ) : (
        <div className="flex flex-col gap-3">
          {filtered.map((p) => (
            <PitchCard
              key={p.id}
              participant={p}
              onWatch={() => setWatching(p)}
              onApprove={() =>
                mutation.mutate({ id: p.id, status: "approved", participant: p, event })
              }
              onReject={() => setWatching(p)}
              pending={mutation.isPending}
            />
          ))}
        </div>
      )}

      <PitchReviewModal
        participant={watching}
        comment={rejectComment}
        setComment={setRejectComment}
        onClose={() => {
          setWatching(null);
          setRejectComment("");
        }}
        onApprove={() =>
          watching &&
          mutation.mutate({
            id: watching.id,
            status: "approved",
            participant: watching,
            event,
          })
        }
        onReject={() =>
          watching &&
          mutation.mutate({
            id: watching.id,
            status: "rejected",
            participant: watching,
            event,
            comment: rejectComment.trim() || undefined,
          })
        }
        pending={mutation.isPending}
      />
    </>
  );
}

function PitchReviewModal({
  participant,
  comment,
  setComment,
  onClose,
  onApprove,
  onReject,
  pending,
}: {
  participant: ParticipantRow | null;
  comment: string;
  setComment: (v: string) => void;
  onClose: () => void;
  onApprove: () => void;
  onReject: () => void;
  pending: boolean;
}) {
  const { data: videoUrl, isLoading: videoLoading, error } = useQuery({
    queryKey: ["pitch-video", participant?.id, participant?.video_storage_path],
    queryFn: () =>
      participant?.video_storage_path
        ? getOrganizerVideoUrl(participant.video_storage_path)
        : Promise.resolve(""),
    enabled: Boolean(participant?.video_storage_path),
    staleTime: 4 * 60 * 1000,
  });

  if (!participant) return null;

  const flags = participant.ai_flags ?? [];
  const needsReview =
    participant.pitch_status === "flagged" || participant.pitch_status === "submitted";
  const roleLine =
    [participant.role, participant.company].filter(Boolean).join(" · ") || participant.email;

  return (
    <Modal
      open
      onClose={onClose}
      title={participant.name}
      description={roleLine}
      maxWidth={520}
      dimBackdrop={false}
    >
      <div className="space-y-4">
        {participant.video_storage_path ? (
          videoLoading ? (
            <div className="mx-auto grid aspect-square w-full max-w-[min(40vh,360px)] place-items-center rounded-full bg-[#f8fafc] text-[13px] text-[#6c757d]">
              Loading video...
            </div>
          ) : error ? (
            <InlineAlert tone="danger">
              Could not load video: {error instanceof Error ? error.message : "unknown error"}
            </InlineAlert>
          ) : videoUrl ? (
            <video
              src={videoUrl}
              controls
              playsInline
              className="mx-auto aspect-square w-full max-w-[min(40vh,360px)] rounded-full border-2 border-[#2563eb] object-cover"
            />
          ) : null
        ) : (
          <div className="mx-auto grid aspect-square w-full max-w-[min(40vh,360px)] place-items-center rounded-full bg-[#f8fafc] text-[13px] text-[#6c757d]">
            No video recorded yet
          </div>
        )}

        {needsReview && flags.length > 0 ? (
          <Card tone="flagged" padding="sm">
            <p className="text-[11px] font-bold uppercase tracking-wide text-[#92400e]">AI flags</p>
            <ul className="mt-1 space-y-0.5">
              {flags.map((f) => (
                <li key={f} className="text-[13px] text-[#78350f]">
                  · {f}
                </li>
              ))}
            </ul>
          </Card>
        ) : null}

        {needsReview ? (
          <div className="space-y-3 border-t border-[#f1f5f9] pt-4">
            <Field
              label="Optional comment to the participant"
              hint="Sent in the rejection email so they know what to fix."
            >
              <Textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="The pitch is too short — please record at least 30 seconds and end with how you can help others."
                rows={2}
              />
            </Field>
            <div className="flex gap-2">
              <Button variant="destructive" onClick={onReject} loading={pending} fullWidth size="md">
                Reject & Request Re-record
              </Button>
              <Button onClick={onApprove} loading={pending} fullWidth size="md">
                Approve Pitch
              </Button>
            </div>
          </div>
        ) : null}
      </div>
    </Modal>
  );
}

function PitchCard({
  participant,
  onWatch,
  onApprove,
  onReject,
  pending,
}: {
  participant: ParticipantRow;
  onWatch: () => void;
  onApprove: () => void;
  onReject: () => void;
  pending: boolean;
}) {
  const isApproved = participant.pitch_status === "approved";
  const isRejected = participant.pitch_status === "rejected";
  const flags = participant.ai_flags ?? [];
  const needsReview =
    participant.pitch_status === "flagged" || participant.pitch_status === "submitted";

  const tone = isApproved
    ? "approved"
    : isRejected
      ? "danger"
      : participant.pitch_status === "flagged"
        ? "flagged"
        : "default";

  return (
    <Card tone={tone} padding="md">
      <div className="flex items-center gap-4">
        <VideoAvatar
          name={participant.name}
          size={56}
          bordered
          videoStoragePath={participant.video_storage_path}
          mode="organizer"
          showPlayBadge
        />
        <div className="flex-1">
          <h3 className="text-[16px] font-bold text-[#1a1a1a]">{participant.name}</h3>
          <p className="text-[13px] text-[#6c757d]">
            {[participant.role, participant.company].filter(Boolean).join(" · ") || participant.email}
          </p>
        </div>
        <PitchStatusBadge status={participant.pitch_status} />
      </div>

      {flags.length > 0 ? (
        <div className="mt-3 rounded-[10px] bg-white px-3 py-2">
          <p className="text-[11px] font-bold uppercase tracking-wide text-[#92400e]">AI detected</p>
          {flags.map((f) => (
            <p key={f} className="text-[13px] text-[#78350f]">
              · {f}
            </p>
          ))}
        </div>
      ) : null}

      <div className="mt-3 flex flex-wrap gap-2">
        <Button variant="secondary" size="sm" onClick={onWatch}>
          Watch Pitch
        </Button>
        {needsReview ? (
          <>
            <Button onClick={onApprove} loading={pending} size="sm">
              Approve
            </Button>
            <Button variant="destructive" size="sm" onClick={onReject} loading={pending}>
              Reject
            </Button>
          </>
        ) : null}
      </div>
    </Card>
  );
}

/**
 * Mutual-matches tab (TZ block D items 9, 15). Lists every pair that
 * has liked each other, with the option to manually pop a single
 * match out of the event-wide reveal timer. The button is hidden
 * once a match is already revealed — either because the global
 * window opened, or because the organizer accelerated it earlier.
 */
function MatchesTab({ event }: { event: EventRow }) {
  const queryClient = useQueryClient();
  const eventId = event.id;
  const globalRevealed = isEventRevealed({
    event_date: event.event_date,
    match_reveal_hours: event.match_reveal_hours,
    reveal_immediately: event.reveal_immediately,
  });

  const { data: matches = [], isLoading } = useQuery({
    queryKey: ["organizer-matches", eventId],
    queryFn: () => fetchEventMatchesForOrganizer(eventId),
    refetchInterval: 30_000,
  });

  const revealMutation = useMutation({
    mutationFn: (matchId: string) => revealMatchManually(matchId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["organizer-matches", eventId] });
    },
    onError: (err: unknown) => {
      window.alert(err instanceof Error ? err.message : "Could not reveal match");
    },
  });

  if (isLoading) {
    return (
      <Card padding="lg">
        <Loader label="Loading matches..." />
      </Card>
    );
  }

  if (matches.length === 0) {
    return (
      <Card padding="lg">
        <EmptyState
          icon="💞"
          title="No mutual matches yet"
          description="Pairs appear here as soon as two people like each other."
        />
      </Card>
    );
  }

  return (
    <>
      <div className="mb-4 flex items-center justify-between">
        <p className="text-[14px] text-[#6c757d]">
          {globalRevealed ? (
            <>Reveal window is open — all matches are already visible to participants.</>
          ) : (
            <>
              {matches.length} {matches.length === 1 ? "pair" : "pairs"} matched so far.
              Reveal individuals early if you want them to start chatting.
            </>
          )}
        </p>
      </div>

      <Card padding="none">
        <ul className="divide-y divide-[#f1f5f9]">
          {matches.map((m) => (
            <MatchRow
              key={m.match_id}
              match={m}
              globalRevealed={globalRevealed}
              pending={
                revealMutation.isPending && revealMutation.variables === m.match_id
              }
              onReveal={() => revealMutation.mutate(m.match_id)}
            />
          ))}
        </ul>
      </Card>
    </>
  );
}

function MatchRow({
  match,
  globalRevealed,
  pending,
  onReveal,
}: {
  match: OrganizerMatchRow;
  globalRevealed: boolean;
  pending: boolean;
  onReveal: () => void;
}) {
  const isAlreadyRevealed =
    globalRevealed || match.manually_revealed_at !== null;

  return (
    <li className="flex items-center justify-between gap-3 px-5 py-3">
      <div className="flex min-w-0 flex-1 items-center gap-3">
        <div className="flex items-center -space-x-2">
          <Avatar name={match.a_name} size={32} />
          <Avatar name={match.b_name} size={32} />
        </div>
        <div className="min-w-0">
          <p className="truncate text-[14px] font-semibold text-[#1a1a1a]">
            {match.a_name} ↔ {match.b_name}
          </p>
          <p className="truncate text-[11px] text-[#94a3b8]">
            Matched {new Date(match.created_at).toLocaleString()}
            {match.manually_revealed_at
              ? ` · Revealed early ${new Date(match.manually_revealed_at).toLocaleString()}`
              : ""}
          </p>
        </div>
      </div>
      {isAlreadyRevealed ? (
        <Badge tone="success">Revealed</Badge>
      ) : (
        <Button size="sm" variant="secondary" onClick={onReveal} disabled={pending}>
          {pending ? "…" : "Reveal early"}
        </Button>
      )}
    </li>
  );
}

function PitchStatusBadge({ status }: { status: ParticipantRow["pitch_status"] }) {
  const tone =
    status === "approved" ? "success" :
    status === "flagged" ? "warning" :
    status === "rejected" ? "danger" :
    status === "submitted" ? "info" :
    "neutral";
  return <Badge tone={tone}>{status}</Badge>;
}

function MetricCard({
  value,
  label,
  progress,
  tone = "neutral",
}: {
  value: string;
  label: string;
  progress?: number;
  tone?: "neutral" | "info" | "warning";
}) {
  const valueColor =
    tone === "info" ? "text-[#2563eb]" :
    tone === "warning" ? "text-[#92400e]" :
    "text-[#1a1a1a]";
  return (
    <Card padding="sm">
      <p className={"text-[24px] font-bold leading-tight " + valueColor}>{value}</p>
      <p className="mt-1 text-[12px] text-[#6c757d]">{label}</p>
      {progress !== undefined ? (
        <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-[#e2e8f0]">
          <div
            className="h-full rounded-full bg-[#2563eb] transition-all duration-500"
            style={{ width: `${progress}%` }}
          />
        </div>
      ) : null}
    </Card>
  );
}

function AnalyticsTab({
  participants,
  event,
}: {
  participants: ParticipantRow[];
  event: EventRow;
}) {
  const timeline = useMemo(() => buildRegistrationTimeline(participants), [participants]);
  const topGoals = useMemo(() => buildTopGoals(participants), [participants]);
  const engagement = useMemo(() => buildEngagement(participants, event), [participants, event]);

  if (participants.length === 0) {
    return (
      <Card padding="lg">
        <EmptyState
          icon="📊"
          title="No data yet"
          description="Analytics appear once participants start registering."
        />
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      <Card padding="md">
        <h3 className="text-[16px] font-bold text-[#1a1a1a]">Registration timeline</h3>
        <p className="mt-1 text-[13px] text-[#6c757d]">
          How participants signed up over time ({timeline.length} day
          {timeline.length === 1 ? "" : "s"})
        </p>
        <TimelineBars points={timeline} />
      </Card>

      <Card padding="md">
        <h3 className="text-[16px] font-bold text-[#1a1a1a]">Top goals</h3>
        <p className="mt-1 text-[13px] text-[#6c757d]">
          Aggregated keywords from "Looking for" answers
        </p>
        {topGoals.length === 0 ? (
          <p className="mt-4 text-[13px] text-[#6c757d]">No goals captured yet.</p>
        ) : (
          <div className="mt-4 flex flex-col gap-2">
            {topGoals.map((g) => (
              <div key={g.term} className="flex items-center justify-between">
                <span className="text-[14px] text-[#1a1a1a]">{g.term}</span>
                <Badge tone="info">
                  {g.count} mention{g.count === 1 ? "" : "s"}
                </Badge>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card padding="md">
        <h3 className="text-[16px] font-bold text-[#1a1a1a]">Engagement</h3>
        <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
          <MetricCard value={`${engagement.conversionPct}%`} label="pitch conversion" />
          <MetricCard value={String(engagement.approved)} label="approved pitches" />
          <MetricCard value={String(engagement.flagged)} label="flagged pitches" />
        </div>
      </Card>
    </div>
  );
}

function ParticipantsTab({
  participants,
  event,
}: {
  participants: ParticipantRow[];
  event: EventRow;
}) {
  const eventId = event.id;
  const eventTitle = event.title;
  const [query, setQuery] = useState("");
  const [adding, setAdding] = useState(false);
  const queryClient = useQueryClient();

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return participants;
    return participants.filter(
      (p) =>
        p.name.toLowerCase().includes(q) ||
        p.email.toLowerCase().includes(q) ||
        (p.role?.toLowerCase().includes(q) ?? false) ||
        (p.company?.toLowerCase().includes(q) ?? false),
    );
  }, [participants, query]);

  // Existing organizer recommendations for this event. Drives the
  // Recommended N · Received N counters per row plus the potential-
  // count computation (we subtract already-paired candidates).
  const { data: existingRecs = [] } = useQuery({
    queryKey: ["event-feed-recs", eventId],
    queryFn: () => fetchEventRecommendations(eventId),
  });

  // Mutual matches for this event. Used to filter already-matched
  // pairs out of the ⚡ N potential count — once a pair is mutual
  // there's nothing left to recommend.
  const { data: eventMatches = [] } = useQuery({
    queryKey: ["organizer-matches", eventId],
    queryFn: () => fetchEventMatchesForOrganizer(eventId),
  });

  // Recommended/received counters per participant. In the
  // bidirectional model these are equal (every rec creates a
  // recipient-row and a candidate-row), but legacy v1 single-
  // direction rows can make them differ — we surface both for
  // transparency.
  const recRecipientCount = useMemo(() => {
    const m = new Map<string, number>();
    for (const r of existingRecs) {
      m.set(r.recipient_participant_id, (m.get(r.recipient_participant_id) ?? 0) + 1);
    }
    return m;
  }, [existingRecs]);
  const recCandidateCount = useMemo(() => {
    const m = new Map<string, number>();
    for (const r of existingRecs) {
      m.set(r.candidate_participant_id, (m.get(r.candidate_participant_id) ?? 0) + 1);
    }
    return m;
  }, [existingRecs]);

  // pair_key set for "already linked" lookups — combines bidirectional
  // recs and existing matches so we can compute potential counts in
  // O(N) without re-querying.
  const pairKey = (a: string, b: string) => (a < b ? `${a}|${b}` : `${b}|${a}`);
  const linkedPairs = useMemo(() => {
    const s = new Set<string>();
    for (const r of existingRecs) {
      s.add(pairKey(r.recipient_participant_id, r.candidate_participant_id));
    }
    for (const m of eventMatches) {
      s.add(pairKey(m.a_id, m.b_id));
    }
    return s;
  }, [existingRecs, eventMatches]);

  // V3 minimum-profile gate: a participant can be a candidate iff
  // they filled BOTH 'looking for' and 'can help with'. Without the
  // pair, the algorithm has nothing to score. Rows below this bar
  // surface the "Invite to complete" CTA instead of Recommend.
  const isProfileComplete = (p: ParticipantRow): boolean =>
    Boolean(p.looking_for?.trim() && p.can_help_with?.trim());

  // ⚡ N potential — count of OTHER participants in the event who
  // (a) have a complete profile, (b) aren't flagged/rejected,
  // (c) aren't already paired (rec OR match) with this participant.
  const potentialCounts = useMemo(() => {
    const m = new Map<string, number>();
    const eligible = participants.filter(
      (p) =>
        isProfileComplete(p) &&
        p.pitch_status !== "flagged" &&
        p.pitch_status !== "rejected",
    );
    for (const a of participants) {
      let n = 0;
      for (const b of eligible) {
        if (a.id === b.id) continue;
        if (linkedPairs.has(pairKey(a.id, b.id))) continue;
        n++;
      }
      m.set(a.id, n);
    }
    return m;
  }, [participants, linkedPairs]);

  // V3: this is now the RECIPIENT we'll recommend candidates to,
  // not the candidate. Frame 02 inverts the previous one-way model.
  const [recommendRecipient, setRecommendRecipient] = useState<ParticipantRow | null>(
    null,
  );

  const [autoSuggestOpen, setAutoSuggestOpen] = useState(false);

  const exportCsv = () => {
    const rows = [
      ["Name", "Email", "Role", "Company", "Contact", "Status", "Registered"],
      ...participants.map((p) => [
        p.name,
        p.email,
        p.role ?? "",
        p.company ?? "",
        p.contact_for_matches ?? "",
        p.pitch_status,
        new Date(p.created_at).toISOString(),
      ]),
    ];
    const csv = rows
      .map((r) => r.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${eventTitle.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}-participants.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#94a3b8]" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search participants..."
            className="pl-10"
          />
        </div>
        <Button onClick={() => setAdding(true)} size="md">
          + Add Participant
        </Button>
        <Button
          variant="secondary"
          onClick={() => setAutoSuggestOpen(true)}
          disabled={participants.length < 2}
          size="md"
        >
          ⚡ Auto-suggest matches
        </Button>
        <Button variant="secondary" onClick={exportCsv} disabled={participants.length === 0} size="md">
          Export CSV
        </Button>
      </div>

      {filtered.length === 0 ? (
        <Card padding="lg">
          <EmptyState
            icon={participants.length === 0 ? "👥" : "🔎"}
            title={
              participants.length === 0 ? "No participants yet" : "No matches for your search"
            }
          />
        </Card>
      ) : (
        <Card padding="none">
          <ul className="divide-y divide-[#f1f5f9]">
            {filtered.map((p) => {
              const recCount = recRecipientCount.get(p.id) ?? 0;
              const receivedCount = recCandidateCount.get(p.id) ?? 0;
              const potential = potentialCounts.get(p.id) ?? 0;
              const profileOk = isProfileComplete(p);
              const hasVideo =
                p.pitch_status === "approved" && Boolean(p.video_storage_path);
              return (
                <li key={p.id} className="px-5 py-4 transition-colors hover:bg-[#f8fafc]">
                  <div className="flex items-start justify-between gap-3">
                    <Link
                      to={`/events/${eventId}/participants/${p.id}`}
                      className="flex flex-1 items-center gap-3 no-underline"
                    >
                      <VideoAvatar
                        name={p.name}
                        size={42}
                        videoStoragePath={p.video_storage_path}
                        mode="organizer"
                      />
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-1.5">
                          <p className="text-[15px] font-bold text-[#1a1a1a]">
                            {p.name}
                          </p>
                          {hasVideo ? (
                            <span className="rounded-[6px] bg-[#eaf1fe] px-1.5 py-0.5 text-[10px] font-semibold tracking-[0.03em] text-[#2563eb]">
                              + VIDEO
                            </span>
                          ) : null}
                          {profileOk && potential > 0 ? (
                            <span className="inline-flex items-center gap-1 rounded-[6px] bg-[#fdf3e2] px-1.5 py-0.5 text-[11px] font-semibold text-[#b4690e]">
                              ⚡ {potential} potential
                            </span>
                          ) : profileOk ? (
                            <span className="inline-flex items-center gap-1 rounded-[6px] bg-[#fdf3e2] px-1.5 py-0.5 text-[11px] font-semibold text-[#b4690e]">
                              ⚡ 0 potential
                            </span>
                          ) : null}
                        </div>
                        <p className="mt-0.5 truncate text-[12.5px] text-[#737373]">
                          {[p.role, p.company].filter(Boolean).join(" · ") || p.email}
                        </p>
                      </div>
                    </Link>
                    <div className="flex flex-col items-end gap-2">
                      <PitchStatusBadge status={p.pitch_status} />
                      {profileOk ? (
                        <button
                          type="button"
                          onClick={() => setRecommendRecipient(p)}
                          className="inline-flex items-center gap-1 rounded-[10px] bg-[#eaf1fe] px-3 py-1.5 text-[12.5px] font-semibold text-[#2563eb] transition hover:bg-[#dbeafe]"
                        >
                          <Sparkles className="h-3.5 w-3.5" />
                          Recommend
                        </button>
                      ) : (
                        <InviteToCompleteButton participantId={p.id} />
                      )}
                    </div>
                  </div>

                  {/* Looking for / Can help with — both visible inline
                      so the organizer can scan the recipient's context
                      without opening the detail page. */}
                  {p.looking_for ? (
                    <p className="mt-3 rounded-r-[6px] border-l-[3px] border-[#f59e0b] bg-[#fffbeb] px-3 py-2 text-[12.5px] leading-snug text-[#78350f]">
                      <span className="font-bold tracking-[0.3px] text-[#b4690e]">
                        Looking for ·{" "}
                      </span>
                      {p.looking_for}
                    </p>
                  ) : null}
                  {p.can_help_with ? (
                    <p className="mt-2 rounded-r-[6px] border-l-[3px] border-[#6fb48c] bg-[#eef6f0] px-3 py-2 text-[12.5px] leading-snug text-[#3f5847]">
                      <span className="font-bold tracking-[0.3px] text-[#3e7c5a]">
                        Can help with ·{" "}
                      </span>
                      {p.can_help_with}
                    </p>
                  ) : null}

                  {/* V3 incomplete-profile warning row — explicit gate
                      message so the disabled CTA above isn't mysterious. */}
                  {!profileOk ? (
                    <p className="mt-2 text-[11.5px] text-[#b4690e]">
                      ⚠ Profile incomplete — no «can help with» yet · can't recommend
                    </p>
                  ) : null}

                  {/* Recommended N · Received N. Equal in pure
                      bidirectional model but legacy rows can make
                      them differ; we show both for transparency. */}
                  {profileOk && (recCount > 0 || receivedCount > 0) ? (
                    <p className="mt-2 inline-flex items-center gap-2 text-[11.5px] text-[#9ca3af]">
                      <span>Recommended {recCount}</span>
                      <span className="h-[3px] w-[3px] rounded-full bg-[#9ca3af]" />
                      <span>Received {receivedCount}</span>
                    </p>
                  ) : null}
                </li>
              );
            })}
          </ul>
        </Card>
      )}

      <AddParticipantModal
        open={adding}
        event={event}
        onClose={() => setAdding(false)}
        onAdded={() => {
          queryClient.invalidateQueries({ queryKey: ["participants", eventId] });
          setAdding(false);
        }}
      />

      <RecommendCandidateModal
        recipient={recommendRecipient}
        eventId={eventId}
        onClose={() => setRecommendRecipient(null)}
        onChanged={() => {
          queryClient.invalidateQueries({ queryKey: ["event-feed-recs", eventId] });
        }}
      />

      <AutoSuggestModal
        open={autoSuggestOpen}
        eventId={eventId}
        onClose={() => setAutoSuggestOpen(false)}
        onApproved={() => {
          queryClient.invalidateQueries({ queryKey: ["event-feed-recs", eventId] });
        }}
      />
    </>
  );
}

/**
 * V3 matchmaking panel — spec frames 02/03/03b.
 *
 * Opens for ONE recipient (the participant the organizer tapped
 * Recommend on). Lists every recommendable candidate ranked by
 * mutual strength, with direct + reverse score, why text, and a
 * single "Recommend to both" CTA that drops the pair into both
 * feeds. State per candidate: default · in both feeds (with undo) ·
 * already matched (dim) · limit reached (disabled).
 *
 * 5-active-recipient limit is enforced server-side, but the panel
 * also disables the CTA when the count hits 5 so the user sees the
 * gate before the click.
 */
const ACTIVE_REC_LIMIT = 5;

function RecommendCandidateModal({
  recipient,
  eventId,
  onClose,
  onChanged,
}: {
  recipient: ParticipantRow | null;
  eventId: string;
  onClose: () => void;
  onChanged: () => void;
}) {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  // V3-8: notify defaults to ON per candidate; the toggle lets the
  // organizer opt OUT of the TG nudge for that one. Tracked as a Set
  // of opted-out candidate ids — keeps the default-on semantics
  // simple and reads cleanly on state changes.
  const [notifyOff, setNotifyOff] = useState<Set<string>>(() => new Set());
  const toggleNotify = (candidateId: string) => {
    setNotifyOff((prev) => {
      const next = new Set(prev);
      if (next.has(candidateId)) next.delete(candidateId);
      else next.add(candidateId);
      return next;
    });
  };

  const candidatesQuery = useQuery({
    queryKey: ["recommend-candidates", recipient?.id],
    queryFn: () => fetchRecommendCandidates(recipient?.id ?? ""),
    enabled: Boolean(recipient),
  });
  const candidates: RecommendCandidateRow[] = candidatesQuery.data ?? [];

  const activeCount = useMemo(
    () => candidates.filter((c) => c.already_recommended).length,
    [candidates],
  );
  const atLimit = activeCount >= ACTIVE_REC_LIMIT;

  const recommendMutation = useMutation({
    mutationFn: async (candidateId: string) => {
      if (!recipient) throw new Error("No recipient");
      await recommendPairBidirectional(recipient.id, candidateId);
      // V3-8: fire-and-forget TG nudge unless the organizer turned
      // it off for this candidate.
      if (!notifyOff.has(candidateId)) {
        fireOrganizerRecPush(eventId, recipient.id, candidateId);
      }
    },
    onError: (err: unknown) => {
      setError(err instanceof Error ? err.message : "Could not recommend");
    },
    onSuccess: () => {
      setError(null);
      queryClient.invalidateQueries({
        queryKey: ["recommend-candidates", recipient?.id],
      });
      onChanged();
    },
  });

  const undoMutation = useMutation({
    mutationFn: async (candidateId: string) => {
      if (!recipient) throw new Error("No recipient");
      await unrecommendPairBidirectional(recipient.id, candidateId);
    },
    onError: (err: unknown) => {
      setError(err instanceof Error ? err.message : "Could not undo");
    },
    onSuccess: () => {
      setError(null);
      queryClient.invalidateQueries({
        queryKey: ["recommend-candidates", recipient?.id],
      });
      onChanged();
    },
  });

  if (!recipient) return null;

  const initials = recipient.name.charAt(0).toUpperCase();
  const subtitle =
    [recipient.role, recipient.company].filter(Boolean).join(" · ") ||
    recipient.email;

  return (
    <Modal open={Boolean(recipient)} onClose={onClose} maxWidth={580}>
      {/* Header — who we're recommending for + their looking-for /
          can-help-with so the organizer has the recipient's context
          while scanning candidates. */}
      <div className="border-b border-[#f1f5f9] pb-4">
        <p className="text-[11px] font-bold uppercase tracking-[0.07em] text-[#94a3b8]">
          Recommend candidates for
        </p>
        <div className="mt-3 flex items-start gap-3">
          <div className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-[#dbeafe] text-[16px] font-bold text-[#2563eb]">
            {initials}
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-[16px] font-bold text-[#1a1a1a]">{recipient.name}</p>
            <p className="mt-0.5 text-[12.5px] text-[#6c757d]">{subtitle}</p>
          </div>
        </div>
        {recipient.looking_for ? (
          <div className="mt-3 rounded-r-[6px] border-l-[3px] border-[#f59e0b] bg-[#fffbeb] px-3 py-2 text-[12.5px] leading-snug text-[#78350f]">
            <span className="font-bold tracking-[0.3px]">Looking for · </span>
            {recipient.looking_for}
          </div>
        ) : null}
        {recipient.can_help_with ? (
          <div className="mt-2 rounded-r-[6px] border-l-[3px] border-[#6fb48c] bg-[#eef6f0] px-3 py-2 text-[12.5px] leading-snug text-[#3f5847]">
            <span className="font-bold tracking-[0.3px] text-[#3e7c5a]">
              Can help with ·{" "}
            </span>
            {recipient.can_help_with}
          </div>
        ) : null}
      </div>

      <div className="flex items-center justify-between border-b border-[#f1f5f9] px-1 py-3">
        <p className="text-[12.5px] font-semibold text-[#52525b]">
          {candidatesQuery.isLoading
            ? "Loading…"
            : `${candidates.length} potential candidate${candidates.length === 1 ? "" : "s"}`}
        </p>
        <p className="text-[11.5px] text-[#94a3b8]">Sorted by mutual strength</p>
      </div>

      <div className="max-h-[420px] overflow-y-auto py-2">
        {candidatesQuery.isLoading ? (
          <p className="py-6 text-center text-[13px] text-[#94a3b8]">
            Loading candidates…
          </p>
        ) : candidates.length === 0 ? (
          <p className="py-6 text-center text-[13px] text-[#94a3b8]">
            No participants with a complete profile yet — once they fill
            "looking for" and "can help with" they'll show up here.
          </p>
        ) : (
          <ul className="flex flex-col gap-2">
            {candidates.map((c) => (
              <CandidateCard
                key={c.id}
                candidate={c}
                atLimit={atLimit}
                pending={
                  (recommendMutation.isPending && recommendMutation.variables === c.id) ||
                  (undoMutation.isPending && undoMutation.variables === c.id)
                }
                notifyOn={!notifyOff.has(c.id)}
                onToggleNotify={() => toggleNotify(c.id)}
                onRecommend={() => recommendMutation.mutate(c.id)}
                onUndo={() => undoMutation.mutate(c.id)}
              />
            ))}
          </ul>
        )}
      </div>

      {error ? (
        <InlineAlert tone="danger" className="mt-3">
          {error}
        </InlineAlert>
      ) : null}

      <div className="mt-3 flex items-center justify-between border-t border-[#f1f5f9] pt-3">
        <p
          className={`text-[12.5px] ${atLimit ? "font-semibold text-[#b4690e]" : "text-[#52525b]"}`}
        >
          {atLimit ? "⚠ " : ""}
          <span className="font-bold">
            {activeCount} / {ACTIVE_REC_LIMIT}
          </span>{" "}
          active recommendations
          {atLimit ? " — remove one to add more" : ""}
        </p>
        <Button variant="secondary" onClick={onClose}>
          Done
        </Button>
      </div>
    </Modal>
  );
}

/**
 * V3-11 spec frame 04 — auto-suggested pairs queue.
 *
 * Opens from the Participants tab header. Pulls the top pairs by
 * mutual strength via find_event_auto_suggest_pairs and lets the
 * organizer Approve (= bidirectional rec) / Skip down the list.
 * Skip is local-only — pairs come back on the next open. Approved
 * pairs disappear because they no longer satisfy the "not already
 * linked" filter on the SQL side.
 */
function AutoSuggestModal({
  open,
  eventId,
  onClose,
  onApproved,
}: {
  open: boolean;
  eventId: string;
  onClose: () => void;
  onApproved: () => void;
}) {
  const queryClient = useQueryClient();
  const [skippedKeys, setSkippedKeys] = useState<Set<string>>(() => new Set());
  const [error, setError] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ["auto-suggest-pairs", eventId],
    queryFn: () => fetchAutoSuggestPairs(eventId, 50),
    enabled: open,
  });

  const approveMutation = useMutation({
    mutationFn: async (vars: { a: string; b: string }) => {
      await recommendPairBidirectional(vars.a, vars.b);
    },
    onError: (err: unknown) => {
      setError(err instanceof Error ? err.message : "Could not approve");
    },
    onSuccess: () => {
      setError(null);
      queryClient.invalidateQueries({
        queryKey: ["auto-suggest-pairs", eventId],
      });
      onApproved();
    },
  });

  const pairKey = (a: string, b: string) => (a < b ? `${a}|${b}` : `${b}|${a}`);
  const visiblePairs = useMemo(
    () =>
      (query.data ?? []).filter(
        (p) => !skippedKeys.has(pairKey(p.a_id, p.b_id)),
      ),
    [query.data, skippedKeys],
  );

  if (!open) return null;

  return (
    <Modal open={open} onClose={onClose} maxWidth={680}>
      <div className="border-b border-[#f1f5f9] pb-3">
        <p className="text-[11px] font-bold uppercase tracking-[0.07em] text-[#94a3b8]">
          Auto-suggest matches
        </p>
        <h2 className="mt-2 text-[18px] font-bold tracking-tight text-[#1a1a1a]">
          Suggested pairs
        </h2>
        <p className="mt-1 text-[12.5px] text-[#737373]">
          Top pairs ranked by mutual strength. Approve drops the pair into both
          feeds; skip just hides it for now. Manual recommendations from the
          per-participant panel live on top — this layer doesn't touch them.
        </p>
      </div>

      <div className="flex items-center justify-between py-3 text-[12.5px]">
        <span className="font-semibold text-[#52525b]">
          {query.isLoading
            ? "Loading…"
            : `${visiblePairs.length} pending`}
        </span>
        <span className="text-[#94a3b8]">Sorted by mutual strength</span>
      </div>

      <div className="max-h-[480px] overflow-y-auto">
        {query.isLoading ? (
          <p className="py-6 text-center text-[13px] text-[#94a3b8]">
            Computing pairs…
          </p>
        ) : visiblePairs.length === 0 ? (
          <p className="py-6 text-center text-[13px] text-[#94a3b8]">
            No pairs to suggest. Either everyone is already linked, or there
            aren't enough profile-complete participants yet.
          </p>
        ) : (
          <ul className="flex flex-col gap-2">
            {visiblePairs.map((pair) => (
              <AutoSuggestPairRowView
                key={pairKey(pair.a_id, pair.b_id)}
                pair={pair}
                pending={
                  approveMutation.isPending &&
                  approveMutation.variables?.a === pair.a_id &&
                  approveMutation.variables?.b === pair.b_id
                }
                onApprove={() =>
                  approveMutation.mutate({ a: pair.a_id, b: pair.b_id })
                }
                onSkip={() =>
                  setSkippedKeys((prev) => {
                    const next = new Set(prev);
                    next.add(pairKey(pair.a_id, pair.b_id));
                    return next;
                  })
                }
              />
            ))}
          </ul>
        )}
      </div>

      {error ? (
        <InlineAlert tone="danger" className="mt-3">
          {error}
        </InlineAlert>
      ) : null}

      <div className="mt-3 flex justify-end border-t border-[#f1f5f9] pt-3">
        <Button variant="secondary" onClick={onClose}>
          Done
        </Button>
      </div>
    </Modal>
  );
}

function AutoSuggestPairRowView({
  pair,
  pending,
  onApprove,
  onSkip,
}: {
  pair: AutoSuggestPairRow;
  pending: boolean;
  onApprove: () => void;
  onSkip: () => void;
}) {
  const why = pair.direct_reason || pair.reverse_reason || "";
  const initialsA = pair.a_name.charAt(0).toUpperCase();
  const initialsB = pair.b_name.charAt(0).toUpperCase();
  return (
    <li className="rounded-[14px] border border-[#eaebee] bg-white p-4">
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2">
          <div className="grid h-9 w-9 place-items-center rounded-full bg-[#e5ecfb] text-[13px] font-bold text-[#2563eb]">
            {initialsA}
          </div>
          <span className="text-[14.5px] font-semibold text-[#1a1a1a]">
            {pair.a_name}
          </span>
        </div>
        <span className="text-[16px] text-[#d6357f]">💕</span>
        <div className="flex items-center gap-2">
          <div className="grid h-9 w-9 place-items-center rounded-full bg-[#e5ecfb] text-[13px] font-bold text-[#2563eb]">
            {initialsB}
          </div>
          <span className="text-[14.5px] font-semibold text-[#1a1a1a]">
            {pair.b_name}
          </span>
        </div>
        <div className="ml-auto text-right">
          <div className="text-[18px] font-bold leading-none text-[#2563eb]">
            {pair.mutual_strength}
          </div>
          <div className="mt-0.5 text-[11px] text-[#9ca3af]">mutual</div>
        </div>
      </div>

      {why ? (
        <p className="mt-3 line-clamp-2 text-[13px] leading-snug text-[#52525b]">
          {why}
        </p>
      ) : null}

      <div className="mt-3 flex items-center gap-2">
        <button
          type="button"
          disabled={pending}
          onClick={onApprove}
          className="flex-1 rounded-[10px] bg-[#2563eb] px-4 py-2 text-[13px] font-semibold text-white transition hover:bg-[#1d4ed8] disabled:opacity-60"
        >
          {pending ? "…" : "Approve"}
        </button>
        <button
          type="button"
          onClick={onSkip}
          disabled={pending}
          className="rounded-[10px] border border-[#eaebee] bg-white px-4 py-2 text-[13px] font-semibold text-[#737373] hover:bg-[#f8fafc] disabled:opacity-50"
        >
          Skip
        </button>
      </div>
    </li>
  );
}

function InviteToCompleteButton({ participantId }: { participantId: string }) {
  const [state, setState] = useState<"idle" | "sending" | "sent" | "error">(
    "idle",
  );
  const onClick = async () => {
    setState("sending");
    try {
      await sendCompleteProfileInvite(participantId);
      setState("sent");
      setTimeout(() => setState("idle"), 3000);
    } catch {
      setState("error");
      setTimeout(() => setState("idle"), 3000);
    }
  };
  const label =
    state === "sending"
      ? "Sending…"
      : state === "sent"
        ? "✓ Sent"
        : state === "error"
          ? "Try again"
          : "Invite to complete";
  return (
    <button
      type="button"
      disabled={state === "sending"}
      onClick={onClick}
      title="DM or email the participant to add 'can help with'"
      className="inline-flex items-center gap-1 rounded-[10px] bg-[#fdf3e2] px-3 py-1.5 text-[12.5px] font-semibold text-[#b4690e] transition hover:bg-[#fbe9c5] disabled:opacity-60"
    >
      {label}
    </button>
  );
}

function CandidateCard({
  candidate,
  atLimit,
  pending,
  notifyOn,
  onToggleNotify,
  onRecommend,
  onUndo,
}: {
  candidate: RecommendCandidateRow;
  atLimit: boolean;
  pending: boolean;
  notifyOn: boolean;
  onToggleNotify: () => void;
  onRecommend: () => void;
  onUndo: () => void;
}) {
  const tier = candidate.has_video ? "video" : "profile";
  const subtitle =
    [candidate.role, candidate.company].filter(Boolean).join(" · ") || "—";
  const initials = candidate.name.charAt(0).toUpperCase();

  const directScore = candidate.direct_score ?? 0;
  const reverseScore = candidate.reverse_score ?? 0;

  // why text — prefer direct_reason, with the reverse_reason hint
  // appended on a second line so the organizer sees both sides at a
  // glance. We strip ai-speak ellipses and trailing whitespace.
  const directReason = candidate.direct_reason?.trim() ?? "";
  const reverseReason = candidate.reverse_reason?.trim() ?? "";
  const showLowConfidence = !candidate.has_video;

  const dim = candidate.already_matched;
  const inFeed = candidate.already_recommended && !candidate.already_matched;
  const recommendable = !candidate.already_recommended && !candidate.already_matched;

  return (
    <li
      className={`rounded-[14px] border border-[#eaebee] bg-white p-4 transition ${dim ? "opacity-60" : ""}`}
    >
      <div className="flex items-center gap-3">
        <div className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-[#e5ecfb] text-[14px] font-bold text-[#2563eb]">
          {initials}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-1.5">
            <p className="truncate text-[15px] font-bold text-[#1a1a1a]">
              {candidate.name}
            </p>
            {tier === "video" ? (
              <span className="rounded-[6px] bg-[#eaf1fe] px-1.5 py-0.5 text-[10px] font-semibold tracking-[0.03em] text-[#2563eb]">
                + VIDEO
              </span>
            ) : (
              <span className="rounded-[6px] bg-[#f1f3f5] px-1.5 py-0.5 text-[10px] font-semibold tracking-[0.03em] text-[#6b7280]">
                PROFILE ONLY
              </span>
            )}
          </div>
          <p className="mt-0.5 truncate text-[12.5px] text-[#737373]">
            {subtitle}
          </p>
        </div>
        <div className="text-right">
          <div className="text-[20px] font-bold leading-none text-[#2563eb]">
            {directScore}
          </div>
          <div className="mt-1 inline-flex items-center gap-1 text-[11px] text-[#9ca3af]">
            ← {reverseScore} back
          </div>
        </div>
      </div>

      {directReason || reverseReason ? (
        <p className="mt-3 text-[13px] leading-snug text-[#3f3f46]">
          {directReason || reverseReason}
          {showLowConfidence ? (
            <span className="ml-1 text-[12px] text-[#9ca3af]">
              · goals-only, lower confidence
            </span>
          ) : null}
        </p>
      ) : null}

      <div className="mt-3">
        {dim ? (
          <span className="inline-flex w-full items-center justify-center gap-1.5 rounded-[10px] bg-[#fdf1f6] px-3 py-2 text-[12.5px] font-semibold text-[#d6357f]">
            💕 Mutual match
          </span>
        ) : inFeed ? (
          <div className="flex items-center gap-2">
            <span className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-[10px] bg-[#eef6f0] px-3 py-2 text-[12.5px] font-semibold text-[#3e7c5a]">
              ✓ In both feeds {ageHint(candidate.recommended_at)}
            </span>
            <button
              type="button"
              disabled={pending}
              onClick={onUndo}
              className="rounded-[10px] border border-[#eaebee] bg-white px-3 py-2 text-[12.5px] font-semibold text-[#737373] hover:bg-[#f8fafc] disabled:opacity-50"
            >
              {pending ? "…" : "Undo"}
            </button>
          </div>
        ) : recommendable ? (
          <div className="flex items-center gap-2">
            <button
              type="button"
              disabled={pending || atLimit}
              onClick={onRecommend}
              className={`flex flex-1 items-center justify-center gap-1.5 rounded-[10px] px-3 py-2 text-[13px] font-semibold transition ${
                atLimit
                  ? "bg-[#edeef1] text-[#9ca3af]"
                  : "bg-[#2563eb] text-white hover:bg-[#1d4ed8]"
              } disabled:cursor-not-allowed`}
            >
              {atLimit
                ? "Limit reached"
                : pending
                  ? "…"
                  : "★ Recommend to both"}
            </button>
            {/* V3-8 notify toggle — defaults ON; flips into OFF
                state if the organizer wants a quiet recommendation. */}
            <button
              type="button"
              onClick={onToggleNotify}
              disabled={atLimit}
              className="inline-flex items-center gap-2 whitespace-nowrap text-[12px] font-semibold text-[#52525b] disabled:opacity-50"
            >
              Notify
              <span
                className={`relative inline-block h-[20px] w-[34px] rounded-[11px] transition ${
                  notifyOn ? "bg-[#2563eb]" : "bg-[#d7dae0]"
                }`}
              >
                <span
                  className={`absolute top-[2px] h-[16px] w-[16px] rounded-full bg-white transition-all ${
                    notifyOn ? "left-[16px]" : "left-[2px]"
                  }`}
                />
              </span>
            </button>
          </div>
        ) : null}
      </div>
    </li>
  );
}

function ageHint(iso: string | null): string {
  if (!iso) return "";
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return "· just now";
  const mins = Math.floor(ms / 60_000);
  if (mins < 60) return `· ${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `· ${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `· ${days}d ago`;
}

function AddParticipantModal({
  open,
  event,
  onClose,
  onAdded,
}: {
  open: boolean;
  event: EventRow;
  onClose: () => void;
  onAdded: () => void;
}) {
  const [form, setForm] = useState({ name: "", email: "", role: "", company: "" });
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: async () => {
      const participant = await addParticipantManually(event.id, {
        name: form.name.trim(),
        email: form.email.trim(),
        role: form.role.trim() || undefined,
        company: form.company.trim() || undefined,
      });
      if (event.slug) {
        await enqueueMeetupEmail({
          eventId: event.id,
          toEmail: participant.email,
          toName: participant.name,
          emailType: "meetup_invitation",
          participantId: participant.id,
          payload: {
            name: participant.name,
            eventTitle: event.title,
            eventDate: formatMeetupDate(event.event_date),
            eventUrl: meetupEventUrl(event.slug),
            venue: event.location ?? undefined,
          },
        });
      }
      return participant;
    },
    onSuccess: onAdded,
    onError: (err) => setError(err instanceof Error ? err.message : "Could not add"),
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Add Participant"
      description="Use this to onboard someone who registered outside the system. They'll appear with status pending."
      maxWidth={480}
    >
      {error ? <InlineAlert tone="danger" className="mb-4">{error}</InlineAlert> : null}

      <div className="flex flex-col gap-4">
        <Field label="Name" required>
          <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="Full name" />
        </Field>
        <Field label="Email" required>
          <Input
            type="email"
            value={form.email}
            onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
            placeholder="participant@email.com"
          />
        </Field>
        <Field label="Role">
          <Input
            value={form.role}
            onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}
            placeholder="Founder, Investor, Designer…"
          />
        </Field>
        <Field label="Company">
          <Input
            value={form.company}
            onChange={(e) => setForm((f) => ({ ...f, company: e.target.value }))}
            placeholder="Company name"
          />
        </Field>
      </div>

      <div className="mt-6 flex gap-3">
        <Button variant="secondary" onClick={onClose} size="md">
          Cancel
        </Button>
        <Button
          onClick={() => mutation.mutate()}
          loading={mutation.isPending}
          disabled={!form.name.trim() || !form.email.trim()}
          fullWidth
          size="md"
        >
          {mutation.isPending ? "Adding..." : "Add Participant"}
        </Button>
      </div>
    </Modal>
  );
}

interface TimelinePoint {
  day: string;
  count: number;
}

function buildRegistrationTimeline(participants: ParticipantRow[]): TimelinePoint[] {
  const byDay = new Map<string, number>();
  for (const p of participants) {
    const day = new Date(p.created_at).toISOString().slice(0, 10);
    byDay.set(day, (byDay.get(day) ?? 0) + 1);
  }
  return [...byDay.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([day, count]) => ({ day, count }));
}

function TimelineBars({ points }: { points: TimelinePoint[] }) {
  if (points.length === 0) {
    return <p className="mt-4 text-[13px] text-[#6c757d]">No registrations yet.</p>;
  }
  const max = Math.max(...points.map((p) => p.count), 1);
  return (
    <div className="mt-4 flex items-end gap-2 overflow-x-auto pb-1">
      {points.map((p) => (
        <div key={p.day} className="flex flex-col items-center gap-1">
          <span className="text-[11px] font-semibold text-[#1a1a1a]">{p.count}</span>
          <div className="flex h-[120px] w-10 items-end">
            <div
              className="w-full rounded-t bg-[#2563eb] transition-all duration-500"
              style={{ height: `${(p.count / max) * 100}%` }}
              title={`${p.count} on ${p.day}`}
            />
          </div>
          <span className="text-[10px] text-[#6c757d]">
            {new Date(p.day).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
          </span>
        </div>
      ))}
    </div>
  );
}

const STOPWORDS = new Set([
  "the","a","an","and","or","but","for","to","of","in","on","with","at","by",
  "is","are","was","were","be","been","being","have","has","had","do","does",
  "did","i","you","he","she","it","we","they","me","him","her","us","them",
  "my","your","his","its","our","their","this","that","these","those","as",
  "if","then","so","than","about","up","down","out","over","under","again",
  "once","more","most","some","such","no","not","only","own","same","very",
  "can","will","just","should","now","who","what","where","when","why","how",
  "any","each","both","few","other","which",
]);

function buildTopGoals(participants: ParticipantRow[]): { term: string; count: number }[] {
  const counter = new Map<string, number>();
  for (const p of participants) {
    const text = [p.looking_for, p.can_help_with].filter(Boolean).join(" ").toLowerCase();
    if (!text) continue;
    const words = text.match(/[a-zа-яё0-9][a-zа-яё0-9-]{2,}/gi) ?? [];
    const seen = new Set<string>();
    for (const w of words) {
      if (STOPWORDS.has(w) || seen.has(w)) continue;
      seen.add(w);
      counter.set(w, (counter.get(w) ?? 0) + 1);
    }
  }
  return [...counter.entries()]
    .filter(([, c]) => c >= 2)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 8)
    .map(([term, count]) => ({ term, count }));
}

function buildEngagement(
  participants: ParticipantRow[],
  _event: EventRow,
): { conversionPct: number; approved: number; flagged: number } {
  const total = participants.length;
  const submitted = participants.filter(
    (p) => p.pitch_status === "submitted" || p.pitch_status === "approved" || p.pitch_status === "flagged",
  ).length;
  return {
    conversionPct: total > 0 ? Math.round((submitted / total) * 100) : 0,
    approved: participants.filter((p) => p.pitch_status === "approved").length,
    flagged: participants.filter((p) => p.pitch_status === "flagged").length,
  };
}
